import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import MasterSection from "../../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { Tabs, formatDate, useLoad } from "../../../components/ui";

// Dealership Setup → Workshop: labour operations (standard times), labour rates by pay type and
// location, and service contracts (prepaid plans shown at check-in).
const m = (key) => ({ load: () => dmsService.listMaster(key), save: (id, data) => dmsService.saveMaster(key, id, data) });
const CATEGORIES = [["SERVICE", "Service"], ["REPAIR", "Repair"], ["DIAGNOSIS", "Diagnosis"], ["BODY", "Body"], ["PAINT", "Paint"], ["RECALL", "Recall"]];
const PAY = [["CUSTOMER", "Customer"], ["WARRANTY", "Warranty"], ["INTERNAL", "Internal"]];
const opt = (list) => list.map(([value, label]) => ({ label, value }));
const labelOf = (list, v) => (list.find(([x]) => x === v) || [])[1] || v || "—";

export default function WorkshopSetup() {
  const [section, setSection] = useState("ops");
  const dms = useDmsOptions(["makes"]);
  const fin = useFinOptions(["locations"]);
  const customers = useLoad(() => finService.listCustomers({ size: 500 }), []);
  const customerOptions = (customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.account_number }));
  return (
    <>
      <Tabs
        tabs={[
          { key: "ops", label: "Labour operations" },
          { key: "rates", label: "Labour rates" },
          { key: "contracts", label: "Service contracts" },
        ]}
        active={section}
        onChange={setSection}
      />
      {section === "ops" ? (
        <MasterSection
          {...m("labour-operations")}
          noun="labour operation"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Hours", (r) => Number(r.standard_hours)], ["Make", (r) => r.make_name || "Any"], ["Category", (r) => labelOf(CATEGORIES, r.category)]]}
          fieldKeys={["name", "standard_hours", "make_id", "category"]}
          lockedOnEdit={["code"]}
          toForm={(r) => ({ ...r, make_id: optionByCode(dms.makes, r.make_code) })}
          fields={() => [
            { key: "code", label: "Code", required: true, max: 32, hint: "Letters, digits, - and _" },
            { key: "name", label: "Name", required: true, max: 150 },
            { key: "standard_hours", label: "Standard hours", type: "number", step: "any", min: 0, required: true },
            { key: "make_id", label: "Make (blank: any)", type: "select", options: dms.makes || [] },
            { key: "category", label: "Category", type: "select", options: opt(CATEGORIES) },
          ]}
        />
      ) : null}
      {section === "rates" ? (
        <MasterSection
          {...m("labour-rates")}
          noun="labour rate"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Pay type", (r) => labelOf(PAY, r.pay_type)], ["Location", (r) => r.location_name || "All"], ["Per hour", (r) => fmtMoney(r.rate)]]}
          fieldKeys={["name", "pay_type", "location_id", "rate"]}
          lockedOnEdit={["code"]}
          toForm={(r) => ({ ...r, location_id: optionByCode(fin.locations, r.location_code) })}
          fields={() => [
            { key: "code", label: "Code", required: true, max: 24 },
            { key: "name", label: "Name", required: true, max: 100 },
            { key: "pay_type", label: "Pay type", type: "select", required: true, options: opt(PAY) },
            { key: "location_id", label: "Location (blank: all)", type: "select", options: fin.locations || [] },
            { key: "rate", label: "Rate per hour", type: "number", step: "any", min: 0, required: true },
          ]}
        />
      ) : null}
      {section === "contracts" ? (
        <MasterSection
          {...m("service-contracts")}
          noun="service contract"
          deps={[customers.data]}
          columns={[["Contract", (r) => r.contract_number], ["VIN", (r) => r.vin], ["Plan", (r) => r.plan_name], ["Valid", (r) => `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`], ["Services", (r) => `${r.services_used}/${r.services_included ?? "∞"}`]]}
          fieldKeys={["customer_account_number", "vin", "plan_name", "start_date", "end_date", "km_limit", "services_included", "services_used"]}
          lockedOnEdit={["contract_number"]}
          fields={() => [
            { key: "contract_number", label: "Contract number", required: true, max: 40 },
            { key: "customer_account_number", label: "Customer", type: "select", required: true, options: customerOptions },
            { key: "vin", label: "VIN", required: true, max: 17 },
            { key: "plan_name", label: "Plan", required: true, max: 150 },
            { key: "start_date", label: "From", type: "date", required: true },
            { key: "end_date", label: "To", type: "date", required: true },
            { key: "km_limit", label: "Up to km", type: "number", min: 0 },
            { key: "services_included", label: "Services included", type: "number", min: 0 },
            { key: "services_used", label: "Services used", type: "number", min: 0 },
          ]}
        />
      ) : null}
    </>
  );
}
